import { Text, type UseModalState } from "@clipboard-health/ui-components";
import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { LegacyTwoButtonDialogActions } from "@src/appV2/lib/Dialogs/LegacyDialogActions";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { useRouteMatch } from "react-router-dom";

import { type ShiftBlockWithMappedDetails } from "../api/useFetchPaginatedShiftBlocks";
import { useRequestToBlockShiftBlock } from "../api/useRequestToBookShiftBlock";
import { SHIFT_BLOCK_ACTIONS } from "../utils";

export interface RequestToBookShiftBlockDialogProps {
  modalState: UseModalState;
  shifts: ShiftBlockWithMappedDetails["shifts"];
  shiftBlockId: string;
  agentId: string;
  facilityId: string;
}
export function RequestToBookShiftBlockDialog(props: RequestToBookShiftBlockDialogProps) {
  const { modalState, shifts, shiftBlockId, agentId, facilityId } = props;
  const { showErrorToast, showSuccessToast } = useToast();
  const { path: currentRouterUrl } = useRouteMatch();
  const {
    mutateAsync: mutateRequestToBookShiftBlock,
    isLoading: isLoadingRequestToBookShiftBlock,
  } = useRequestToBlockShiftBlock({
    shiftBlockId,
  });

  return (
    <Dialog open={modalState.modalIsOpen}>
      <DialogTitle>Request This Block</DialogTitle>

      <DialogContent>
        <Text align="center">
          After you request this block, we will confirm the block with the facility.
        </Text>
        <br />
        <Text align="center">
          Once this block is booked, you are committed to working all shifts in the block and all
          other requests will be cancelled.
        </Text>
      </DialogContent>
      <LegacyTwoButtonDialogActions
        leftAction={
          <Button
            fullWidth
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
                action: SHIFT_BLOCK_ACTIONS.DISCARD_REQUEST_THIS_BLOCK_TAPPED,
                shiftBlockId,
                facilityId,
                totalShifts: shifts.length,
                location: currentRouterUrl,
              });
              modalState.closeModal();
            }}
          >
            Cancel
          </Button>
        }
        rightAction={
          <LoadingButton
            fullWidth
            isLoading={isLoadingRequestToBookShiftBlock}
            onClick={async () => {
              try {
                await mutateRequestToBookShiftBlock({
                  data: {
                    type: "shift-block-booking-request",
                    attributes: {
                      agentId,
                    },
                  },
                });
                logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
                  action: SHIFT_BLOCK_ACTIONS.CONFIRM_REQUEST_THIS_BLOCK_TAPPED,
                  shiftBlockId,
                  facilityId,
                  totalShifts: shifts.length,
                  location: currentRouterUrl,
                });
                showSuccessToast("We've received your request.We'll be in touch with you shortly!");
              } catch {
                showErrorToast("Something went wrong while requesting to book the shift block.");
              } finally {
                modalState.closeModal();
              }
            }}
          >
            Request
          </LoadingButton>
        }
      />
    </Dialog>
  );
}
